import axios from 'axios';
import dayjs from 'dayjs';

export const isEmailValid = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
};

export const standardizeDateFormat = (date: string) => {
  const formattedDate = date.split('/').map(part => part.padStart(2, '0')).join('/');
  const parsedDate = dayjs(formattedDate, 'MM/DD/YYYY');
  return parsedDate.format('DD-MM-YYYY');
};

export const getFormattedDate = (date: Date) => {
  const month = date.getMonth() + 1;
  const strMonth = month.toString().padStart(2, '0');
  const day = date.getDate();
  const strDay = day.toString().padStart(2, '0');
  const year = date.getFullYear();
  return strDay + '/' + strMonth + '/' + year;
};

function padTo2Digits(num: number) {
  return num.toString().padStart(2, '0');
}

export const formatDate = (date: Date) => {
  return [
    padTo2Digits(date.getDate()),
    padTo2Digits(date.getMonth() + 1),
    date.getFullYear(),
  ].join('/');
};

export const truncateText = (text: string | undefined, maxLength: number) => {
  if (!text) {
    return '';
  }
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export const formatDateForReport = (date: Date) => {
  return dayjs(date).format('YYYY-MM-DDTHH:mm:ss.SSS');
};

export const getAdjustedOffsetDate = (date: Date) => {
  const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000); // Adjust for timezone offset
  const year = adjustedDate.getFullYear();
  const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
  const day = String(adjustedDate.getDate()).padStart(2, '0');
  const hours = String(adjustedDate.getHours()).padStart(2, '0');
  const minutes = String(adjustedDate.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const formatDateHourString = (dateString: string) => {
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}`;
};

export const compareStringsAsLower = (a: string, b: string): number => {
  return a.toLowerCase().localeCompare(b.toLowerCase());
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const compare = <T>(a: T, b: T, sortField: keyof T, sortDir: 'asc' | 'desc'): number => {
  const aValue = a[sortField];
  const bValue = b[sortField];

  if (sortDir === 'asc') {
    if (aValue > bValue) {
      return 1;
    } else if (aValue < bValue) {
      return -1;
    } else {
      return 0;
    }
  } else if (sortDir === 'desc') {
    if (aValue > bValue) {
      return -1;
    } else if (aValue < bValue) {
      return 1;
    } else {
      return 0;
    }
  }

  return 0;
};

// Function to format the date in DD/MM/YYYY
export const formatDateString = (inputDateString: string): string => {
  if (inputDateString) {
    const inputDate = new Date(inputDateString);

    // Check if the date is valid
    if (!isNaN(inputDate.getTime())) {
      const day = inputDate.getDate().toString().padStart(2, '0');
      const month = (inputDate.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
      const year = inputDate.getFullYear();

      return `${day}/${month}/${year}`;
    } else {
      return 'Invalid Date';
    }
  }
  else
    return '';
};

export const applyRoundAndFormat = (value: number, currencyCode?: string, maxFractionDigits = 2) => {
  const fallbackCurrencyCode = 'GBP';
  const roundedValue = Math.round(value * 1e8) / 1e8; // Round to 8 decimal places
  // minimumFractionDigits must not exceed maximumFractionDigits, otherwise Intl.NumberFormat throws
  const minFractionDigits = Math.min(2, maxFractionDigits);

  // Validate currency code - use fallback if empty, null, undefined, or invalid
  const validCurrencyCode = currencyCode && currencyCode.trim() !== '' ? currencyCode : fallbackCurrencyCode;

  // Custom format for MXN to avoid the "MX$" prefix
  if (validCurrencyCode === 'MXN') {
    return `$${new Intl.NumberFormat(undefined, {
      style: 'decimal', // Use 'decimal' style to avoid any currency-specific symbols
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
    }).format(roundedValue)}`;
  }
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: validCurrencyCode,
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  }).format(roundedValue);
};

export const applyRoundAndFormatDecimal = (value: number, maxFractionDigits = 2) => {
  const roundedValue = Math.round(value * 1e8) / 1e8;
  const minFractionDigits = Math.min(2, maxFractionDigits);
  return new Intl.NumberFormat(undefined, {
    style: 'decimal',
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  }).format(roundedValue);
};

export const formatPromotionReservationDuration = (minutes?: number | null) => {
  if (!minutes || minutes <= 0) {
    return null;
  }

  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }

  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }

  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
};

export const formatPalletQty = (casesPerPallet?: number | null): string => {
  return casesPerPallet != null && casesPerPallet > 0 ? casesPerPallet.toString() : '—';
};

// Case % represents the fill contribution of a single case towards a full pallet
// (100 / casesPerPallet), letting customers work out how many more cases to add
// to reach a target pallet fill percentage without doing the maths themselves.
export const getCaseFillPercent = (casesPerPallet?: number | null): number | null => {
  if (!casesPerPallet || casesPerPallet <= 0) {
    return null;
  }
  return 100 / casesPerPallet;
};

export const formatCaseFillPercent = (casesPerPallet?: number | null): string => {
  const percent = getCaseFillPercent(casesPerPallet);
  return percent == null ? '—' : `${percent.toFixed(2)}%`;
};

export const exportFile = (data: Blob, type: string, title: string) => {
  const blob = new Blob([data], { type: type });

  const downloadLink = document.createElement('a');
  downloadLink.href = URL.createObjectURL(blob);
  downloadLink.download = title;

  document.body.appendChild(downloadLink);
  downloadLink.click();

  document.body.removeChild(downloadLink);
};

export const generateSasDownload = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const convertFileToImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.src = reader.result as string;
      resolve(img);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const getFileFromBase64 = (b64Data: string, fileName: string) => {
  const contentType = 'image';
  const sliceSize = 512;

  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  const file = new File([blob], fileName, { type: 'image/png' });
  return file;
};

export const base64ToBlob = (base64: string, type = 'image/jpg') => {
  const base64Data = base64.replace(/^data:.+;base64,/, '');
  const binaryString = window.atob(base64Data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type });
};

export const adjustDateByDays = (date: Date, days: number, endDate?: boolean): Date => {
  const adjustedDate = new Date(date);
  adjustedDate.setDate(adjustedDate.getDate() + days);
  if (endDate) {
    adjustedDate.setUTCHours(23, 59, 59, 0);
  } else {
    adjustedDate.setUTCHours(0, 0, 0, 0);
  }

  return adjustedDate;
};

export const getCurrencySymbol = (currencyCode: string) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  });

  let currencySymbol = '';
  formatter.formatToParts().forEach(part => {
    if (part.type === 'currency') {
      currencySymbol = part.value;
    }
  });

  return currencySymbol;
};

export const roundToSignificantDigit = (value: number) => {
  const fixedValue = 2;
  let precision = 0;

  let val = value - Math.round(value);

  if (val === 0) {
    return parseFloat(value.toFixed(fixedValue));
  }

  while (Math.abs(val) < 1) {
    val *= 10;
    precision++;
  }

  if (precision === 1) {
    return parseFloat(value.toFixed(fixedValue));
  }

  return parseFloat(value.toFixed(precision));
};

export const extractErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.message;
  } else {
    return error as string;
  }
};

export const formatNumber = (value: number | null | undefined, minDecimals = 0, maxDecimals = 3, useGrouping = true): string => {
  if (value == null || isNaN(value)) {
    return ''; // Invalid input
  }

  const minDecimalsAdjusted = Number.isInteger(value) ? 0 : minDecimals;

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: minDecimalsAdjusted,
    maximumFractionDigits: maxDecimals,
    useGrouping,
  }).format(value);
};

export const getEnumValues = <T extends object>(enumObj: T): { name: string, value: number }[] => {
  return Object.keys(enumObj)
    .filter(key => isNaN(Number(key)))
    .map(key => ({
      name: key,
      value: enumObj[key as keyof T] as unknown as number,
    }));
};

export function hasUnassignedZone(zones: { name?: string }[]): boolean {
  return zones.some(z => z.name?.toLowerCase().startsWith('unassigned -'));
}

export const formatDateTimeForDisplay = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');

  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const yyyy = date.getFullYear();
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  const ss = pad(date.getSeconds());

  return `${mm}-${dd}-${yyyy} ${hh}:${min}:${ss}`;
};


export const getDateFormatByFranchise = (franchise?: string | null, useDashSeparator: boolean = false): string => {
  switch (franchise) {
    case 'Rustan Philippines':
      return 'MM/DD/YYYY';
    default:
      return useDashSeparator ? 'DD-MM-YYYY' : 'DD/MM/YYYY';
  }
};

const getLocaleAndOptions = (franchise?: string | null): { locale: string, options: Intl.DateTimeFormatOptions } => {
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  let locale = 'en-GB';
  if (franchise === 'Rustan Philippines') {
    locale = 'en-US';
  }
  return { locale, options };
};

export const getInventorySku = (item: { gtin?: string | null; sku?: string | null }): string => {
  const gtin = item.gtin ?? '';
  const sku = item.sku ?? '';
  return sku.trim() !== '' ? sku : gtin;
};

export const convertToJsonNetDate = (date: Date | string | number | null): string | null => {
  if (!date) return null;
  const timestamp = new Date(date).getTime();
  return `/Date(${timestamp})/`;
};

export const convertFromJsonNetDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
};

export const handleSearchFieldKeyDown = (
  event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
  search: () => void,
) => {
  if (event.key === 'Enter') {
    event.preventDefault(); // stops form submit / page reload
    search();
  }
};

export { calculateItemParQuantity, clampParQuantity, parseNumericStock } from './parQuantity';
